import axios from 'axios';
import { get as getCookie } from 'es-cookie';
import { CMProfile } from '@/models/cm-profile.model';
import { CMError } from '@/models/cm-error.model';
import { CMProfileCompany } from '@/models/cm-profile-company.model';
import { IProfileSchoolData, CMProfileSchool } from '@/models/cm-profile-school.model';
import { IProfileInterestData, CMProfileInterest } from '@/models/cm-profile-interest.model';


const ax = axios.create({
    baseURL: process.env.VUE_APP_PROFILE_API_ENDPOINT || '//my.codemotion.com/api',
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + _retrieveToken(),
    },
});
interface CMResponse {
    data: any;
    errors: any;
    success: boolean;
}

export interface CMServerErrorList extends Error {
    errors: CMServerError[];
}

export interface CMServerError extends Error {
    name: string;
    message: string;
}

export interface RawBasicProfileData {
    name: string;
    surname: string;
    headline: string;
    socials: RawProfileSocial[];
}

export interface RawProfileSocial {
    name: string;
    link: string;
    _type: string;
}

export interface IProfileCompanyData {
    _id?: string;
    name: string;
    location: string;
    role: string;
    size: string;

    isCurrentCompany: boolean;

    monthFrom: number;
    yearFrom: number;

    monthTo: number;
    yearTo: number;
}

export interface RawContactInfoData {
    nationality: string;
    basedIn: string;
    basedInR: string;
    basedInC: string;
}

export function _retrieveToken(): string {
    let token;
    token = getCookie('cmtkn');
    if (!token) { token = localStorage.getItem('cmtkn'); }
    if (!token) { token = ''; }
    return token;
}

export class CMPRofileApi {

    public static async ping(): Promise<string> {
        const res = await ax.get<CMResponse>('/ping');
        return res.data.data;
    }

    public static async getConf(): Promise<any> {
        try {
            const res = await ax.get<CMResponse>('/conf');
            return res.data.data;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async getUserProfile(): Promise<CMProfile> {
        try {
            const res = await ax.get<CMResponse>('/me?full=1');
            const profile = (new CMProfile()).fromRaw(res.data.data.profile);
            (<any>profile).email = res.data.data.email;
            (<any>profile).uid = res.data.data.uid;
            profile.roles = res.data.data.roles;

            return profile;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateBasic(data: RawBasicProfileData): Promise<boolean> {
        try {
            const res = await ax.put<CMResponse>('/me/basic', data);
            return res.data.success;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateContantInfo(data: RawContactInfoData) {
        try {
            const res = await ax.put<CMResponse>('/me/contact', data);
            return res.data.success;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateBio(bio: string): Promise<boolean> {
        try {
            const res = await ax.put<CMResponse>('/me/bio', { bio });
            return res.data.success;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async addCompany(company: IProfileCompanyData): Promise<CMProfileCompany[]> {
        try {
            const res = await ax.post<CMResponse>('/me/companies', company);
            const companies: CMProfileCompany[] = [];
            res.data.data.forEach((c) => {
                companies.push(new CMProfileCompany().fromRaw(c));
            });
            return companies;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateCompany(company: IProfileCompanyData): Promise<CMProfileCompany[]> {
        if (!company._id) { throw new CMError('CMInvalidCompanyDataError', 'Invalid company update data'); }
        try {
            const res = await ax.put<CMResponse>('/me/companies/' + company._id, company);
            const companies: CMProfileCompany[] = [];
            res.data.data.forEach((c) => {
                companies.push(new CMProfileCompany().fromRaw(c));
            });
            return companies;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async deleteCompany(companyId: string): Promise<CMProfileCompany[]> {
        // tslint:disable-next-line:max-line-length
        if (!companyId || companyId === '') { throw new CMError('CMInvalidCompanyDataError', 'Invalid company update data'); }
        try {
            const res = await ax.delete('/me/companies/' + companyId);
            const companies: CMProfileCompany[] = [];
            res.data.data.forEach((c) => {
                companies.push(new CMProfileCompany().fromRaw(c));
            });
            return companies;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async addSchool(school: IProfileSchoolData): Promise<CMProfileSchool[]> {
        try {
            const res = await ax.post<CMResponse>('/me/schools', school);
            const schools: CMProfileSchool[] = [];
            res.data.data.forEach((c) => {
                schools.push(new CMProfileSchool().fromRaw(c));
            });
            return schools;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateSchool(school: IProfileSchoolData): Promise<CMProfileSchool[]> {
        if (!school._id) { throw new CMError('CMInvalidCompanyDataError', 'Invalid company update data'); }
        try {
            const res = await ax.put<CMResponse>('/me/schools/' + school._id, school);
            const schools: CMProfileSchool[] = [];
            res.data.data.forEach((c) => {
                schools.push(new CMProfileSchool().fromRaw(c));
            });
            return schools;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async deleteSchool(schoolId: string): Promise<CMProfileSchool[]> {
        // tslint:disable-next-line:max-line-length
        if (!schoolId || schoolId === '') { throw new CMError('CMInvalidCompanyDataError', 'Invalid company update data'); }
        try {
            const res = await ax.delete('/me/schools/' + schoolId);
            const schools: CMProfileSchool[] = [];
            res.data.data.forEach((c) => {
                schools.push(new CMProfileSchool().fromRaw(c));
            });
            return schools;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateLookingForJob(looking: boolean): Promise<boolean> {
        try {
            const res = await ax.put('/me/lookingForJob', { looking });
            return true;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateInterests(interests: string[]): Promise<CMProfileInterest[]> {
        try {
            const res = await ax.put('/me/interests', interests);
            return res.data.data.map((i) => {
                return (new CMProfileInterest()).fromRaw(i);
            });
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateCommunities(communities: string[]): Promise<string[]> {
        try {
            const res = await ax.put('/me/communities', communities);
            return res.data.data;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateLanguages(languages: string[]): Promise<string[]> {
        try {
            const res = await ax.put('/me/languages', languages);
            return res.data.data;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async becomeAuthor(): Promise<string[]> {
        try {
            const res = await ax.get('/become/author');
            return res.data.data;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async updateJobsData(payload){
        try {
            const res = await ax.put('/me/jobData', payload);
            const profile = (new CMProfile()).fromRaw(res.data.data);
            return profile
        } catch (e) {
            throw this._handleError(e);
        }
    }   

    public static async getJobPositionsList(full: boolean = false) {
        let url = '/job/positions';
        if(full){
            url += '?full=1'
        }
        try {
            const res = await ax.get(url);
            return res.data.data;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    public static async getJobSkillsList() {
        try {
            const res = await ax.get('/job/skills');
            return res.data.data;
        } catch (e) {
            throw this._handleError(e);
        }
    }

    private static _handleError(error): CMServerErrorList | Error {
        if (error.response) {
            if (error.response.status === 401) {
                return new CMError('UnauthorizedRedirect', error.response.data.errors[0].message);
            }
            if (this._checkTokenDelete(error.response.data.errors)) {
                return new CMError('Unauthorized', 'Invalid or expired token');
            }
            return {
                errors: error.response.data.errors,
                name: 'CMServerErrorList',
                message: 'Server Error',
            };
        } else if (error.request) {
            return new Error('Error on request => Server does not respond');
        } else {
            return new Error('Client error => ' + error.message);
        }
    }

    private static _checkTokenDelete(errors) {
        if (!errors) { return false; }
        const tError = errors.findIndex((elem) => {
            return elem.name === CM_INVALID_TOKEN || elem.name === CM_EXPIRED_TOKEN;
        });
        return tError !== -1;
    }

}

export const CM_INVALID_TOKEN = 'CMInvalidTokenError';
export const CM_EXPIRED_TOKEN = 'CMExpiredTokenError';
export const CM_DUPLICATED_EMAIL_ERROR = 'CMDuplicatedEmailError';
export const CM_DUPLICATED_EMAIL_SPEAKER_ERROR = 'CMDuplicatedEmailSpeakerError';
export const CM_INVALID_EMAIL_ERROR = 'CMInvalidEmailError';
export const CM_INVALID_PASSWORD_ERROR = 'CMInvalidPasswordError';
export const CM_INVALID_CONFIRM_PASSWORD_ERROR = 'CMInvalidConfirmPasswordError';
export const CM_INVALID_REF_ID_ERROR = 'CMInvalidRefIdError';
export const CM_INVALID_CONSENTS_ERROR = 'CMInvalidConsentsError';
export const CM_INVALID_REQUIRED_CONSENTS_ERROR = 'CMInvalidRequiredConsentsError';
export const CM_INVALID_OPTIONAL_CONSENTS_ERROR = 'CMInvalidOptionalConsentsError';
export const CM_UNHANDLED_ERROR = 'CMUnhandledError';
export const CM_INVALID_LOGIN_DATA_ERROR = 'CMInvalidLoginData';
export const CM_DB_ERROR = 'DBError';
export const CM_INVALID_EMAIL_OR_TOKEN_ERROR = 'CMInvalidEmailOrTokenError';
export const CM_PASSWORD_TOKEN_EXPIRED_ERROR = 'CMPasswordTokenExpiredError';
export const CM_PROFILE_NOT_FOUND_ERROR = 'CMProfileNotFoundError';
